import type { ComponentProps } from "react";
import { FlowHub } from "@/components/ui/atoms/flow-hub";
import { FlowTile } from "@/components/ui/atoms/flow-tile";
import { Arcade } from "@/components/ui/atoms/icons/arcade";
import {
  FlowDiagram,
  type FlowDiagramProps,
} from "@/components/ui/molecules/flow-diagram";
import { MCP_CLIENT_METADATA, type McpClientId } from "@/metadata/mcp-clients";
import { TOOLKIT_ICON_MAP } from "@/metadata/toolkit-icons";
import { TOOLKITS } from "@/metadata/toolkits";

/**
 * Default client IDs shown on the left when none are provided. These map
 * into `MCP_CLIENT_METADATA` for label + icon resolution.
 */
export const DEFAULT_MCP_CLIENT_IDS: readonly McpClientId[] = [
  "claude_code",
  "cursor",
  "vscode",
  "codex",
  "goose",
  "windsurf",
];

/**
 * Default toolkit IDs shown on the right when none are provided. These
 * map into `TOOLKIT_ICON_MAP` (for the icon) and `TOOLKITS` (for the
 * label).
 */
export const DEFAULT_MCP_SERVER_IDS: readonly string[] = [
  "Gmail",
  "Slack",
  "Github",
  "Linear",
  "Notion",
  "Jira",
  "Hubspot",
  "GoogleCalendar",
  "GoogleDrive",
  "Salesforce",
  "Stripe",
];

const ARCADE_HUB_CLASS_NAMES =
  "shadow-amber-950/25 shadow-xl bg-[linear-gradient(135deg,oklch(from_var(--brand-yellow)_l_c_h_/_0.55),oklch(from_var(--brand-green)_l_c_h_/_0.4),oklch(from_var(--brand-fuchsia)_l_c_h_/_0.45))]";

type GatewayDiagramProps = {
  /** MCP client identifiers shown on the left. */
  mcpClientIds?: readonly McpClientId[];
  /** Toolkit identifiers shown on the right (MCP servers). */
  mcpServerIds?: readonly string[];
  animate?: FlowDiagramProps["animate"];
  animation?: FlowDiagramProps["animation"];
  lineStyle?: FlowDiagramProps["lineStyle"];
  size?: FlowDiagramProps["size"];
} & Omit<ComponentProps<"div">, "children">;

/**
 * Arcade-branded preset of `FlowDiagram` configured for MCP gateway
 * visualisations. Pass just IDs — the template resolves labels + icons
 * from the design-system metadata registries.
 *
 * - Left side: MCP clients (Claude, Cursor, VS Code, etc.) resolved via
 *   `MCP_CLIENT_METADATA`.
 * - Right side: toolkits (Gmail, Slack, GitHub, etc.) resolved via
 *   `TOOLKIT_ICON_MAP` and `TOOLKITS`.
 * - Center: `FlowHub` with the Arcade gradient and Arcade logo.
 *
 * Unknown IDs are silently filtered out — typically caught at compile
 * time by the `McpClientId` type, but defensive at runtime.
 */
const HUB_SIZE_BY_FLOW_SIZE = {
  sm: "md",
  md: "lg",
  lg: "xl",
} as const;

function GatewayDiagram({
  mcpClientIds = DEFAULT_MCP_CLIENT_IDS,
  mcpServerIds = DEFAULT_MCP_SERVER_IDS,
  animate = "both",
  animation = "flow",
  lineStyle,
  size = "md",
  "aria-label": ariaLabel = "Arcade MCP gateway diagram",
  ...props
}: GatewayDiagramProps) {
  const tileSize = size;
  const hubSize = HUB_SIZE_BY_FLOW_SIZE[size];

  const clientTiles = mcpClientIds
    .map((id) => {
      const entry = MCP_CLIENT_METADATA[id];
      if (!entry) {
        return null;
      }
      const { label, Icon } = entry;
      return (
        <FlowTile key={id} label={label} size={tileSize}>
          <Icon />
        </FlowTile>
      );
    })
    .filter((node) => node !== null);

  const serverTiles = mcpServerIds
    .map((id) => {
      const Icon = TOOLKIT_ICON_MAP[id];
      if (!Icon) {
        return null;
      }
      const label = TOOLKITS.find((toolkit) => toolkit.id === id)?.label ?? id;
      return (
        <FlowTile key={id} label={label} size={tileSize}>
          <Icon />
        </FlowTile>
      );
    })
    .filter((node) => node !== null);

  return (
    <FlowDiagram
      animate={animate}
      animation={animation}
      aria-label={ariaLabel}
      center={
        <FlowHub className={ARCADE_HUB_CLASS_NAMES} size={hubSize}>
          <Arcade aria-hidden />
        </FlowHub>
      }
      left={clientTiles}
      lineStyle={lineStyle}
      right={serverTiles}
      size={size}
      {...props}
    />
  );
}

export { GatewayDiagram };
export type { GatewayDiagramProps };
